import { z } from 'zod';
import { ObjectIdSchema } from '../../shared/object-id.schema';
import { AssigneeModelSchema } from '../../shared/assignee-model.schema';
import { ProcessDraftSchema, ProcessDraftStepSchema, ProcessDraftAssignmentSchema } from '../../entities/process-draft';
import { ProcessDraftNoteSchema } from '../../entities/process-note';
import { StepInputSchema } from '../../shared/step-input.schema';
import { ProcessStepAssignmentStatusSchema } from '../../entities/process-step-assignment/process-step-assignment.schema';
import { PopulatedUserSchema, UserDisplayIdentitySchema } from '../../entities/user/user.schema';
import { PopulatedOrgFunctionSchema } from '../../entities/org-function/org-function.schema';
import { PopulatedSystemSchema } from '../../entities/system/system.schema';
import { PopulatedExternalContactSchema } from '../../entities/external-contact/external-contact.schema';
import { EntitySuggestionSchema } from '../../entities/entity-suggestion/entity-suggestion.schema';
import { StepConflictSchema } from '../publish-process-draft/publish-process-draft.schema';
import { ProcessDraftEventNodeSchema } from '../../entities/process-node';
import { CapacityBindingSchema, CapacitySourcesSchema } from '../../shared/capacity';
/**
 * Active-survey counts for one step (ADR 0009), shown in the editor. Soft-deleted
 * surveys (departed performers) are excluded server-side.
 */ export const DraftStepSurveyStatusSchema = z.object({
    pending: z.number(),
    completed: z.number(),
    dismissed: z.number(),
    /**
   * One entry per performer of the step: their display name + state. `pending`/
   * `completed` reflect an existing survey; `dismissed` is a performer who
   * answered "Ich mache diesen Schritt nicht" (ADR 0022) — attributed here on
   * purpose, because correcting the performer set needs the name; `toCreate` is
   * a performer who has no survey yet — publish will create one (e.g. a new
   * step, or an added member); `invited` is a whoElseDoesThis peer still
   * awaiting acceptance — surfaced so the modeler sees them, but not surveyed
   * until they accept (ADR 0009).
   */ recipients: z.array(UserDisplayIdentitySchema.extend({
        status: z.enum([
            'pending',
            'completed',
            'dismissed',
            'toCreate',
            'invited'
        ]),
        /**
       * Where this performer stands on the Variant Check, which `status` cannot
       * express: a classic survey left `pending` says nothing about whether the
       * variant question was ever put to them (`notApplicable` — author,
       * assigned User, or the check is off and no nomination is pending for
       * them) or already answered. A decline is `answered` like any other
       * response; `status: 'dismissed'` carries the *what*.
       */ variantStatus: z.enum([
            'notApplicable',
            'pending',
            'answered'
        ])
    }))
});
/**
 * Open assignment surfaced on the draft graph: an in-flight viral invitation
 * on the published process (or a pre-publish draft assignment) that hasn't
 * produced a recording yet. Rendered as a placeholder node in its lane with
 * incoming edges drawn from `inputs[].sourceSteps`.
 *
 * `stableStepId` is the graph-topology id — downstream steps' `inputs[]
 * .sourceSteps[].stepId` resolves against this, and once the invitee records
 * the resulting `ProcessStepRecording._id` equals `stableStepId`. Draft and
 * published placeholders travel the same wire shape.
 */ export const OpenDraftAssignmentSchema = z.object({
    _id: ObjectIdSchema,
    stableStepId: ObjectIdSchema,
    /**
   * Populated entity for a resolved assignee, or a thin
   * `{ type: 'suggestion', id }` ref when the placeholder is assigned to a
   * still-pending EntitySuggestion. The flow builder resolves the suggestion
   * arm to its proposed name (blue "proposed" node) via `suggestionsById`.
   */ assignedTo: z.union([
        PopulatedUserSchema,
        PopulatedOrgFunctionSchema,
        PopulatedSystemSchema,
        PopulatedExternalContactSchema,
        z.object({
            type: z.literal('suggestion'),
            id: z.string()
        })
    ]),
    assignedToModel: AssigneeModelSchema,
    inputs: z.array(StepInputSchema),
    status: ProcessStepAssignmentStatusSchema
});
/**
 * Persistent undo/redo timeline meta for the draft (ADR 0007). The editor
 * renders the undo/redo buttons from this — it survives reload because the
 * timeline lives server-side, not in an in-memory stack.
 *
 * `cursor` is the count of applied history entries (0 = at the draft's
 * baseline). Concurrency between a user's tabs is handled server-side (an
 * internal CAS on the timeline), so the client supplies no precondition —
 * undo/redo always return authoritative fresh state.
 */ export const DraftHistoryMetaSchema = z.object({
    canUndo: z.boolean(),
    canRedo: z.boolean(),
    cursor: z.number().int().nonnegative()
});
/**
 * GET /api/v2/process/:id/draft — response
 *
 * `liveConflict` is non-null when any live step recording has been modified
 * after `draft.createdAt`. Carries the same per-step field diff returned by
 * the publish 409, so the editor surfaces both load-time and publish-time
 * conflicts through the same dialog.
 *
 * `entitySuggestions` carries every suggestion referenced by a `suggestion`-typed
 * union ref across the draft's steps/assignments (any status), so the editor can
 * resolve a proposed Team/System to its name on the canvas and in the detail
 * panel. Draft steps don't denormalize a per-step `entitySuggestions[]` the way
 * recordings do, so they're collected and fetched at read time here.
 */ export const GetProcessDraftResponseSchema = z.object({
    draft: ProcessDraftSchema,
    steps: z.array(ProcessDraftStepSchema),
    draftAssignments: z.array(ProcessDraftAssignmentSchema),
    openAssignments: z.array(OpenDraftAssignmentSchema),
    entitySuggestions: z.array(EntitySuggestionSchema),
    liveConflict: z.array(StepConflictSchema).nullable(),
    /**
   * Event node rows of the draft (ADR 0003). Optional during the rollout
   * window only — the backend always ships it since issue 07. Distinct from
   * the legacy embedded `draft.events[]`. Draft rows carry
   * `processId`/`draftId`/`stableStepId`, hence the draft arm of the event
   * node schema (the live arm's required `process` can't describe them).
   */ events: z.array(ProcessDraftEventNodeSchema).optional(),
    /**
   * Undo/redo timeline meta (ADR 0007 — draft history). Always present — a
   * draft with no edits yet reports `{ canUndo: false, canRedo: false,
   * cursor: 0 }`.
   */ history: DraftHistoryMetaSchema,
    /**
   * Draft sticky notes (`processdraftnotes`, ADR 0008). Seeded from the live
   * process's notes on draft creation, edited with undo, and promoted back to
   * live on publish. A note with empty `targets` is a free, process-wide
   * comment.
   */ notes: z.array(ProcessDraftNoteSchema),
    /**
   * Per-step survey status keyed by `stableStepId` (ADR 0009): counts of active
   * (non-deleted) surveys by state, so the editor can show whether a step
   * already has surveys before deciding to re-request. Steps with none are
   * absent from the map. Optional only so existing draft-response fixtures need
   * no update — the backend always sends it.
   */ surveyStatus: z.record(z.string(), DraftStepSurveyStatusSchema).optional(),
    /**
   * Effective capacity per step, keyed by `stableStepId` — the live rows with
   * each draft step's UNPUBLISHED manual value swapped in, so the editor shows
   * what publish would produce. Present for callers holding `ReadSensitive` on
   * the process only (PRD 6.2); every other editor gets no capacity block at
   * all. A draft step with no live recording yet has no entry.
   */ stepCapacities: z.record(z.string(), CapacityBindingSchema).optional(),
    /** Both sources side by side for the same steps, for the shadowed value. */ stepCapacitySources: z.record(z.string(), CapacitySourcesSchema).optional()
});
/**
 * POST /api/v2/process/:id/draft — response
 * (same shape as GET)
 */ export const CreateProcessDraftResponseSchema = GetProcessDraftResponseSchema;
/**
 * POST /api/v2/process/:id/draft/undo|redo — response (same shape as GET)
 *
 * No request body: the operation always acts on the server's current timeline
 * head and returns authoritative fresh state, so a stale tab self-corrects.
 * Genuine concurrent timeline writes (two tabs at once) surface as a 409 from
 * the server-internal CAS, prompting a refetch.
 */ export const UndoRedoDraftResponseSchema = GetProcessDraftResponseSchema;
/**
 * DELETE /api/v2/process/:id/draft — response
 */ export const DiscardProcessDraftResponseSchema = z.object({
    deletedDraftId: ObjectIdSchema
});

//# sourceMappingURL=process-draft.schema.js.map